import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface PaymentEntry {
    id: string;
    investorName: string;
    investorMobile: string;
    paidToName: string;
    paidToMobile: string;
    paymentMethod: 'online' | 'offline';
    paymentImage: string;
    orderId: string;
    date: string;
    status: 'pending' | 'approved' | 'rejected';
    amount?: number;
    isCPFOnly?: boolean; // Flag to indicate this is just CPF details, not a final confirmed payment yet
}

interface PaymentsState {
    entries: PaymentEntry[];
}

const defaultMockPayments: PaymentEntry[] = [
    {
        id: 'PAY-1',
        investorName: 'Rahul Patel',
        investorMobile: '9876543210',
        paidToName: 'Sowmya M',
        paidToMobile: '9999999999',
        paymentMethod: 'online',
        paymentImage: 'https://firebasestorage.googleapis.com/v0/b/markwave-481315.firebasestorage.app/o/placeholders%2Fpy.jpg?alt=media',
        orderId: 'ORD-54321',
        date: new Date(Date.now() - 86400000).toISOString(),
        status: 'approved'
    },
    {
        id: 'PAY-2',
        investorName: 'Amit Sharma',
        investorMobile: '9876543211',
        paidToName: 'Sowmya M',
        paidToMobile: '9999999999',
        paymentMethod: 'offline',
        paymentImage: 'https://firebasestorage.googleapis.com/v0/b/markwave-481315.firebasestorage.app/o/placeholders%2Fpy.jpg?alt=media',
        orderId: 'ORD-54322',
        date: new Date(Date.now() - 172800000).toISOString(),
        status: 'pending'
    }
];

const loadPaymentsFromStorage = (): PaymentEntry[] => {
    try {
        const saved = localStorage.getItem('farmvest_payments');
        if (!saved) return defaultMockPayments;

        const parsed: PaymentEntry[] = JSON.parse(saved);
        return parsed.length > 0 ? parsed : defaultMockPayments;
    } catch (e) {
        console.error('Failed to load payments from storage', e);
        return defaultMockPayments;
    }
};

const initialState: PaymentsState = {
    entries: loadPaymentsFromStorage(),
};

const paymentsSlice = createSlice({
    name: 'payments',
    initialState,
    reducers: {
        addPayment: (state, action: PayloadAction<PaymentEntry>) => {
            // If already exists for this order, update it instead of adding new (for CPF updates)
            const existingIdx = state.entries.findIndex(e => e.orderId === action.payload.orderId);
            if (existingIdx !== -1) {
                state.entries[existingIdx] = { ...state.entries[existingIdx], ...action.payload };
            } else {
                state.entries.unshift(action.payload);
            }
            localStorage.setItem('farmvest_payments', JSON.stringify(state.entries));
        },
        setPayments: (state, action: PayloadAction<PaymentEntry[]>) => {
            state.entries = action.payload;
            localStorage.setItem('farmvest_payments', JSON.stringify(state.entries));
        },
        updatePaymentStatus: (state, action: PayloadAction<{ id: string, status: 'approved' | 'rejected' }>) => {
            const entry = state.entries.find(e => e.id === action.payload.id);
            if (entry) {
                entry.status = action.payload.status;
                entry.isCPFOnly = false; // Once approved/rejected, it's definitely a full payment
                localStorage.setItem('farmvest_payments', JSON.stringify(state.entries));
            }
        },
    },
});

export const { addPayment, setPayments, updatePaymentStatus } = paymentsSlice.actions;
export const paymentsReducer = paymentsSlice.reducer;
